import React, { useEffect, useState } from 'react';

import { ChevronDown, Sun, Moon, Tag } from 'lucide-react';

import ProfileMenu from './ProfileMenu';
import packageJson from '../../../package.json'; // Adjust the path to your `package.json`
import { API_BASE_URL } from '../../config/config';
import { useDarkMode } from '../../hooks/useDarkMode';
import { UserClient } from '../../services/apiClient';
import axiosInstance from '../../services/axiosInstance';
import {
  getUserName,
  getAdminUserName,
  getUserId,
} from '../../utils/tokenUtils';

const Topbar: React.FC = () => {
  const userId = getUserId();
  const userName = getUserName() || '';
  const adminUsername = getAdminUserName();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const [failedPictureUrl, setFailedPictureUrl] = useState<string | null>(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(
    () => {
      // Get stored URL immediately on mount
      const storedData = localStorage.getItem(`profilePic_${userId}`);
      if (storedData) {
        const { url, expiresAt } = JSON.parse(storedData);

        return expiresAt > Date.now() ? url : null; // Only use if not expired
      }

      return null;
    },
  );
  const userClient = new UserClient(API_BASE_URL, axiosInstance);

  const toggleProfileMenu = (): void => {
    setProfileMenuOpen(!profileMenuOpen);
  };

  // Function to get stored SAS URL from localStorage
  const getStoredSasUrl = (): {
    url: string | null;
    expiresAt: number | null;
  } => {
    const storedData = localStorage.getItem(`profilePic_${userId}`);
    if (!storedData) {
      return { url: null, expiresAt: null };
    }

    const { url, expiresAt } = JSON.parse(storedData);

    return { url, expiresAt };
  };

  // Function to save SAS URL in localStorage
  const storeSasUrl = (url: string, expiresInMinutes: number): void => {
    const expiresAt = Date.now() + (expiresInMinutes - 1) * 60 * 1000; // Expire 1 min before the actual expiry
    localStorage.setItem(
      `profilePic_${userId}`,
      JSON.stringify({ url, expiresAt }),
    );
  };

  // Fetch profile picture SAS URL
  useEffect(() => {
    if (!userId) {
      return;
    }

    const { url: storedUrl, expiresAt } = getStoredSasUrl();

    // If stored SAS URL is valid, use it immediately
    if (storedUrl && expiresAt && expiresAt > Date.now()) {
      setProfilePictureUrl(storedUrl);
    }

    const fetchProfilePicture = async (): Promise<void> => {
      try {
        const profilePicResponse =
          await userClient.generateProfilePictureSas(userId);
        if (profilePicResponse.success) {
          const newUrl = profilePicResponse.data ?? '';
          setProfilePictureUrl(newUrl);
          storeSasUrl(newUrl, 5);
        }
      } catch (error) {
        console.error('Error fetching profile picture:', error);
      }
    };

    // Only fetch a new SAS token if it's expired or about to expire
    if (!storedUrl || !expiresAt || expiresAt < Date.now()) {
      fetchProfilePicture();
    }
  }, [userId]);

  const getInitials = (name: string): string => {
    const initials = name
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();

    return initials || 'U';
  };

  return (
    <header className="flex h-20 items-center justify-between border-b border-primary/40 bg-secondary px-4 lg:px-6">
      <div className="flex items-center space-x-4">
        {/* Logo */}
        <a href="/" className="flex items-center">
          <img
            src="/images/logo-white.png"
            alt="GolfPlayed"
            // The wordmark is a ~5:1 lockup, so h-12 renders ~240px wide. On a 375px phone that
            // is two thirds of the bar and it crowds the profile menu. Half height below sm.
            className="h-7 w-auto sm:h-10 lg:h-12"
          />
        </a>

        {/* Version */}
        <div className="ml-4 hidden items-center rounded-full border border-white/15 bg-white/5 px-3 py-1.5 lg:flex">
          <Tag className="mr-2 h-4 w-4 text-primary" />
          <span className="text-sm text-white/70">v.{packageJson.version}</span>
        </div>
      </div>

      {/* Notifications, Messages, Dark Mode Toggle, and Profile */}
      <div className="flex items-center space-x-4">
        {/* Dark Mode Toggle */}
        <button
          onClick={toggleDarkMode}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-primary hover:text-secondary"
          aria-label="Toggle Dark Mode"
        >
          {isDarkMode ? (
            <Sun className="w-5 h-5" aria-hidden="true" />
          ) : (
            <Moon className="w-5 h-5" aria-hidden="true" />
          )}
        </button>

        {/* Profile Dropdown */}
        <div className="relative">
          <button
            className="flex items-center space-x-2 rounded-lg p-2 transition-colors hover:bg-white/10"
            onClick={toggleProfileMenu}
          >
            {/* Profile Picture or Initials */}
            {/* A picture that fails to load falls back to the initials, not a broken image. */}
            {profilePictureUrl && profilePictureUrl !== failedPictureUrl ? (
              <img
                className="h-10 w-10 rounded-full ring-2 ring-primary/50"
                src={profilePictureUrl}
                alt="Profile"
                onError={() => setFailedPictureUrl(profilePictureUrl)}
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-semibold text-secondary">
                {getInitials(userName)}
              </div>
            )}
            <div className="flex flex-col">
              <span className="hidden text-sm font-semibold text-white sm:block">
                {userName}
              </span>
              {adminUsername && (
                <span className="hidden text-xs text-white/50 sm:block">
                  {adminUsername}
                </span>
              )}
            </div>
            <ChevronDown
              className={`h-5 w-5 text-white/60 transition-transform ${
                profileMenuOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {profileMenuOpen && <ProfileMenu />}
        </div>
      </div>
    </header>
  );
};

export default Topbar;
